import {
  type Command,
  type DefaultRecord,
  type Event,
  type GlobalPosition,
  type ReadEvent,
  type StreamPosition,
  command,
  event,
} from '../core/types.js';

/**
 * Utility functions for creating events and commands
 */

// Re-export the core helpers for convenience
export { event, command };

/**
 * Simple event creation with automatic type inference
 * @param type - The event type identifier
 * @param data - The event payload data
 * @param metadata - Optional event metadata
 * @returns A properly typed event instance
 * @public
 * @example
 * ```typescript
 * const userRegistered = createEvent('UserRegistered', {
 *   userId: 'user-123',
 *   email: 'user@example.com'
 * });
 * ```
 */
export function createEvent<
  EventType extends string,
  EventData extends DefaultRecord,
>(
  type: EventType,
  data: EventData,
  metadata?: DefaultRecord
): Event<EventType, EventData, typeof metadata> {
  return metadata !== undefined
    ? event(type, data, metadata)
    : event(type, data, undefined);
}

/**
 * Simple command creation with automatic type inference
 * @param type - The command type identifier
 * @param data - The command payload data
 * @param metadata - Optional command metadata
 * @returns A properly typed command instance
 * @public
 * @example
 * ```typescript
 * const registerUser = createCommand('RegisterUser', {
 *   userId: 'user-123',
 *   email: 'user@example.com'
 * });
 * ```
 */
export function createCommand<
  CommandType extends string,
  CommandData extends DefaultRecord,
>(
  type: CommandType,
  data: CommandData,
  metadata?: DefaultRecord
): Command<CommandType, CommandData, typeof metadata> {
  return metadata !== undefined
    ? command(type, data, metadata)
    : command(type, data, undefined);
}

/**
 * Create a ReadEvent (simulates what would come from event store)
 * This is primarily for testing purposes
 * @param inputEvent - The input event to convert
 * @param streamId - The stream identifier
 * @param streamPosition - The position in the stream
 * @param globalPosition - The global position
 * @param eventId - Optional event ID (generates UUID if not provided)
 * @param schemaVersion - Optional schema version (defaults to "1.0")
 * @param transactionId - Optional transaction ID (generates UUID if not provided)
 * @param createdAt - Optional creation timestamp (defaults to current time)
 * @returns A ReadEvent instance for testing
 * @public
 * @example
 * ```typescript
 * const event = createEvent('UserRegistered', { userId: '123' });
 * const readEvent = createReadEvent(event, 'user-123', 1, 1);
 * ```
 */
export function createReadEvent<EventType extends Event>(
  inputEvent: EventType,
  streamId: string,
  streamPosition: StreamPosition,
  globalPosition: GlobalPosition,
  eventId?: string,
  schemaVersion?: string,
  transactionId?: string,
  createdAt?: string
): ReadEvent<EventType> {
  return {
    type: inputEvent.type,
    data: inputEvent.data,
    metadata: 'metadata' in inputEvent ? inputEvent.metadata : undefined,
    streamId,
    streamPosition,
    globalPosition,
    eventId: eventId || crypto.randomUUID(),
    schemaVersion: schemaVersion || '1.0',
    transactionId: transactionId || crypto.randomUUID(),
    createdAt: createdAt || new Date().toISOString(),
  } as ReadEvent<EventType>;
}
